import { useMemo } from "react";
import moment from "moment-timezone";
import { pick } from "lodash";
import { useScheduleContext } from "../context/useScheduleContext"
import { useDefaultSection } from "./useDefaultSection";
import { useUser } from "../../../../../utils/hooks/user";
import { combineDateAndTime, renderDurationAsFormat } from "../../../../../utils/helpers/date";
// import { calculateSectionsWithAutomaticBreaks, getAutomaticBreaks } from "../../helpers/sections";
// import { useCalculatedSections } from "./useCalculatedSections";

export const calculateSections = (event: any) => {
    const startDate = combineDateAndTime(
        event?.isTemplate ? moment() : moment(event?.date),
        moment(event?.startDate)
    );

    return event?.sections?.map((section: any) => {
        const start = (section?.start || section?.start === 0)
            ? (typeof section?.start === "number"
                ? moment(startDate).clone().add(section.start, "seconds")
                : combineDateAndTime(moment(startDate), moment(section?.start)))
            : undefined;

        const end = section?.end
            ? typeof section?.end === "number"
                ? moment(startDate).clone().add(section.end, "seconds")
                : combineDateAndTime(moment(startDate), moment(section?.end))
            : undefined;

        return {
            ...section,
            start,
            end,
            duration: typeof section.duration === "number" ? renderDurationAsFormat(section.duration, "HH:mm") : section.duration
        }
    });
}

export const useInitialValues = () => {
    const { selectedCellOrEvent, type, resources } = useScheduleContext();
    const { defaultSection, breaks } = useDefaultSection();
    const loggedUser = useUser();

    return useMemo(() => {
        const user = selectedCellOrEvent?.user
            ? (
                typeof selectedCellOrEvent?.user === "string"
                    ? { id: selectedCellOrEvent?.user }
                    : selectedCellOrEvent?.user
            ) : undefined;

        const subjectResource = type === "home"
            ? loggedUser
            : type === "profile"
                ? resources?.[0]
                : undefined;

        if (selectedCellOrEvent?.addAnotherEvent) {
            const defaultValues = pick(
                defaultSection,
                ["payCode", "job", "location", "level1", "level2", "level3", "level4", "skills", "licenses", "certifications"]
            );
            return {
                isSimple: true,
                date: selectedCellOrEvent?.isTemplate
                    ? selectedCellOrEvent?.date
                    : moment(selectedCellOrEvent?.date || selectedCellOrEvent?.startDate).toDate(),
                startDate: undefined,
                endDate: undefined,
                ...defaultValues,
                recurrence: null,
                sections: [defaultValues],
                users: user ? [user] : undefined
            }
        } else if (selectedCellOrEvent?.isCreating && selectedCellOrEvent?.isUnassignedShift) {
            return {
                isSimple: true,
                recurrence: null,
                date: undefined,
                startDate: undefined,
                endDate: undefined,
                payCode: undefined,
                slots: 1
            }
        } else if (selectedCellOrEvent?.isCreating) {
            //simple create
            return {
                isSimple: true,
                recurrence: null,
                ...defaultSection,
                date: selectedCellOrEvent?.isTemplate
                    ? moment(selectedCellOrEvent?.date).format("dddd").toLocaleLowerCase()
                    : moment(selectedCellOrEvent?.date || selectedCellOrEvent?.startDate).toDate(),
                startDate: defaultSection?.start ? moment(defaultSection.start).toDate() : undefined,
                endDate: defaultSection?.end ? moment(defaultSection.end).toDate() : undefined,
                breaks,
                users: subjectResource ? [subjectResource] : undefined
            }
        } else if (selectedCellOrEvent?.isSimple && !selectedCellOrEvent?.isCreating) {
            //simple edit
            const firstSection = selectedCellOrEvent?.sections?.[0];
            const sections = calculateSections(selectedCellOrEvent);
            const breaks = sections
                ?.filter((section: any) => section.type === "break")
                ?.map((br: any) => ({
                    ...br,
                    start: typeof br.start === "number"
                        ? moment(selectedCellOrEvent?.startDate).add(br.start, "seconds").toDate()
                        : br.start,
                    end: typeof br.end === "number"
                        ? moment(selectedCellOrEvent?.startDate).add(br.end, "seconds").toDate()
                        : br.end
                }));

            return {
                // isSimple: true,
                ...selectedCellOrEvent,
                ...firstSection,
                slots: selectedCellOrEvent?.slots ?? 1,
                date: selectedCellOrEvent?.isTemplate
                    ? selectedCellOrEvent?.date
                    : moment(selectedCellOrEvent?.date || selectedCellOrEvent?.startDate).toDate(),
                startDate: selectedCellOrEvent?.startDate ? moment(selectedCellOrEvent.startDate).toDate() : undefined,
                endDate: selectedCellOrEvent?.endDate ? moment(selectedCellOrEvent.endDate).toDate() : undefined,
                sections,
                breaks,
                users: user ? [user] : undefined
            }
        } else {
            //advance edit
            const firstSection = selectedCellOrEvent?.sections?.[0];
            const sections = calculateSections(selectedCellOrEvent);
            const breaks = sections
                ?.filter((section: any) => section.type === "break")
                ?.map((br: any) => ({
                    ...br,
                    start: typeof br.start === "number"
                        ? moment(selectedCellOrEvent?.startDate).add(br.start, "seconds").toDate()
                        : br.start,
                    end: typeof br.end === "number"
                        ? moment(selectedCellOrEvent?.startDate).add(br.end, "seconds").toDate()
                        : br.end
                }));

            return {
                isSimple: true,
                ...selectedCellOrEvent,
                ...firstSection,
                slots: selectedCellOrEvent?.slots ?? 1,
                date: selectedCellOrEvent?.isTemplate
                    ? selectedCellOrEvent?.date
                    : moment(selectedCellOrEvent?.date || selectedCellOrEvent?.startDate).toDate(),
                startDate: selectedCellOrEvent?.startDate ? moment(selectedCellOrEvent.startDate).toDate() : undefined,
                endDate: selectedCellOrEvent?.endDate ? moment(selectedCellOrEvent.endDate).toDate() : undefined,
                sections,
                breaks,
                users: user ? [user] : undefined
            }
        }
    }, [selectedCellOrEvent, defaultSection, breaks, type, resources, loggedUser]);
}
